import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Play,
  Users,
  DollarSign,
  Target,
  Clock,
  TrendingUp,
  AlertCircle
} from 'lucide-react';

const CurrentEventCard = ({ activeEvent }) => {
  const navigate = useNavigate();

  const handleCreateEvent = () => {
    navigate('/admin/events/new');
  };

  if (!activeEvent) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="card p-6"
      >
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-gray-100 dark:bg-dark-700 rounded-full flex items-center justify-center mx-auto mb-4">
            <Play className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            No hay eventos activos
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Crea un nuevo evento para comenzar a gestionar apuestas
          </p>
          <button
            onClick={handleCreateEvent}
            className="btn-primary hover:scale-105 transition-transform"
          >
            <Play className="w-4 h-4 mr-2" />
            Crear Evento
          </button>
        </div>
      </motion.div>
    );
  }

  const getStepColor = (step) => {
    if (step <= 5) return 'success';
    if (step <= 9) return 'warning';
    return 'danger';
  };

  const stepColor = getStepColor(activeEvent.current_step);
  const progressPercentage = (activeEvent.current_step / 13) * 100;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="card p-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Evento Activo
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {activeEvent.name}
          </p>
        </div>

        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-success-500 rounded-full animate-pulse"></div>
          <span className="text-sm font-medium text-success-600 dark:text-success-400">
            En Vivo
          </span>
        </div>
      </div>

      {/* Current Step */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Paso Actual
          </span>
          <span className={`text-sm font-bold ${
            stepColor === 'success' ? 'text-success-600 dark:text-success-400' :
            stepColor === 'warning' ? 'text-warning-600 dark:text-warning-400' :
            'text-danger-600 dark:text-danger-400'
          }`}>
            {activeEvent.current_step} / 13
          </span>
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-gray-200 dark:bg-dark-700 rounded-full h-3 overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${progressPercentage}%` }}
            transition={{ duration: 1, ease: "easeOut" }}
            className={`h-full rounded-full ${
              stepColor === 'success' ? 'bg-gradient-success' :
              stepColor === 'warning' ? 'bg-gradient-warning' :
              'bg-gradient-danger'
            }`}
          />
        </div>

        {/* Risk Indicator */}
        <div className="flex items-center justify-center mt-2">
          <span className={`text-xs px-3 py-1 rounded-full font-medium ${
            stepColor === 'success' ? 'bg-success-100 dark:bg-success-900/30 text-success-700 dark:text-success-300' :
            stepColor === 'warning' ? 'bg-warning-100 dark:bg-warning-900/30 text-warning-700 dark:text-warning-300' :
            'bg-danger-100 dark:bg-danger-900/30 text-danger-700 dark:text-danger-300'
          }`}>
            {stepColor === 'success' ? '🟢 Riesgo Bajo' :
             stepColor === 'warning' ? '🟡 Riesgo Medio' :
             '🔴 Riesgo Alto'}
          </span>
        </div>
      </div>

      {/* Event Stats */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="text-center p-4 bg-gray-50 dark:bg-dark-800 rounded-lg">
          <Users className="w-6 h-6 text-primary-600 dark:text-primary-400 mx-auto mb-2" />
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {activeEvent.participant_count}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Participantes
          </p>
        </div>

        <div className="text-center p-4 bg-gray-50 dark:bg-dark-800 rounded-lg">
          <DollarSign className="w-6 h-6 text-success-600 dark:text-success-400 mx-auto mb-2" />
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            ${Number(activeEvent.currentBetTotal || 0).toLocaleString()}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Apuesta Total
          </p>
        </div>
      </div>

      {/* Event Performance */}
      {activeEvent.stats && (
        <div className="mb-6">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
            Estadísticas del Evento
          </h4>

          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="p-3 bg-success-50 dark:bg-success-900/20 rounded-lg">
              <p className="text-lg font-bold text-success-700 dark:text-success-300">
                {activeEvent.stats.total_draws}
              </p>
              <p className="text-xs text-success-600 dark:text-success-400">
                Empates
              </p>
            </div>

            <div className="p-3 bg-gray-50 dark:bg-dark-700 rounded-lg">
              <p className="text-lg font-bold text-gray-700 dark:text-gray-300">
                {activeEvent.stats.total_no_draws}
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Sin Empate
              </p>
            </div>

            <div className="p-3 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
              <p className="text-lg font-bold text-primary-700 dark:text-primary-300">
                ${Number(activeEvent.stats.total_volume || 0).toLocaleString()}
              </p>
              <p className="text-xs text-primary-600 dark:text-primary-400">
                Volumen
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Participant Details Preview */}
      {activeEvent.participantDetails && activeEvent.participantDetails.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Participantes del Paso
            </h4>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {activeEvent.participantDetails.length} usuarios
            </span>
          </div>

          <div className="space-y-2 max-h-32 overflow-y-auto custom-scrollbar">
            {activeEvent.participantDetails.slice(0, 3).map((participant) => (
              <div key={participant.userId} className="flex items-center justify-between py-2 px-3 bg-gray-50 dark:bg-dark-800 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 bg-gradient-primary rounded-full flex items-center justify-center">
                    <span className="text-white text-xs font-medium">
                      {participant.fullName.charAt(0)}
                    </span>
                  </div>
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {participant.fullName}
                  </span>
                </div>

                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    ${participant.betAmount.toLocaleString()}
                  </span>
                  {!participant.canBet && (
                    <AlertCircle className="w-4 h-4 text-warning-500" />
                  )}
                </div>
              </div>
            ))}

            {activeEvent.participantDetails.length > 3 && (
              <div className="text-center py-2">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  +{activeEvent.participantDetails.length - 3} más...
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex space-x-3 mt-6">
        <button className="flex-1 btn-primary">
          <Target className="w-4 h-4 mr-2" />
          Actualizar Resultado
        </button>
        <button className="btn-secondary px-4">
          <Clock className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );
};

export default CurrentEventCard;